import React, { useMemo } from 'react';
import * as XLSX from 'xlsx-js-style';
import { workbookToXlsxBlob } from '../../utils/workbookToXlsxBlob';
import type { KpiLastUpdatedRow, KpiOperationalAgeingRow } from './types';
import { applyYellowHeaderRowAndAutofilter, setSheetColumnWidths } from './kpiDashboardExcelExport';
import {
  KPI_DASHBOARD_TABLE_GRAND_ROW,
  KPI_DASHBOARD_TABLE_SUBTOTAL_ROW,
  KPI_DASHBOARD_TABLE_TD,
  KPI_DASHBOARD_TABLE_TD_NUM,
  KPI_DASHBOARD_TABLE_TH,
  KPI_DASHBOARD_TABLE_TH_CENTER,
} from './kpiDashboardTableStyles';

const TYPE_ORDER = ['INCIDENT', 'SERVICE_REQUEST'] as const;

function sumAgeing(rows: KpiOperationalAgeingRow[]) {
  return rows.reduce(
    (acc, r) => ({
      days2to5: acc.days2to5 + r.days2to5,
      days6to10: acc.days6to10 + r.days6to10,
      days11to15: acc.days11to15 + r.days11to15,
      days16to30: acc.days16to30 + r.days16to30,
      daysOver30: acc.daysOver30 + r.daysOver30,
      rowTotal: acc.rowTotal + r.rowTotal,
    }),
    {
      days2to5: 0,
      days6to10: 0,
      days11to15: 0,
      days16to30: 0,
      daysOver30: 0,
      rowTotal: 0,
    }
  );
}

function sumLast(rows: KpiLastUpdatedRow[]) {
  return rows.reduce(
    (acc, r) => ({
      within24h: acc.within24h + r.within24h,
      over24h: acc.over24h + r.over24h,
      rowTotal: acc.rowTotal + r.rowTotal,
    }),
    { within24h: 0, over24h: 0, rowTotal: 0 }
  );
}

const tdBase = `${KPI_DASHBOARD_TABLE_TD} bg-white`;
const tdNum = KPI_DASHBOARD_TABLE_TD_NUM;

export interface KpiOperationalAgeingTableProps {
  ageingByGroup: KpiOperationalAgeingRow[];
}

export const KpiOperationalAgeingTable: React.FC<KpiOperationalAgeingTableProps> = ({ ageingByGroup }) => {
  const ageingGrand = useMemo(() => sumAgeing(ageingByGroup), [ageingByGroup]);
  const ageingByType = useMemo(() => {
    const map = new Map<string, KpiOperationalAgeingRow[]>();
    for (const row of ageingByGroup) {
      const list = map.get(row.ticketType) ?? [];
      list.push(row);
      map.set(row.ticketType, list);
    }
    return map;
  }, [ageingByGroup]);

  if (ageingByGroup.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-3 py-4 text-xs text-slate-500">
        No tickets aged 2+ days in these buckets for the current filters.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[720px] border-collapse text-xs sm:text-sm">
        <thead>
          <tr>
            <th className={KPI_DASHBOARD_TABLE_TH}>Type</th>
            <th className={KPI_DASHBOARD_TABLE_TH}>Assignment group</th>
            <th className={KPI_DASHBOARD_TABLE_TH_CENTER}>2–5 days</th>
            <th className={KPI_DASHBOARD_TABLE_TH_CENTER}>6–10 days</th>
            <th className={KPI_DASHBOARD_TABLE_TH_CENTER}>11–15 days</th>
            <th className={KPI_DASHBOARD_TABLE_TH_CENTER}>16–30 days</th>
            <th className={KPI_DASHBOARD_TABLE_TH_CENTER}>&gt;30 days</th>
            <th className={KPI_DASHBOARD_TABLE_TH_CENTER}>Grand total</th>
          </tr>
        </thead>
        <tbody>
          {TYPE_ORDER.map((tt) => {
            const rows = ageingByType.get(tt) ?? [];
            if (rows.length === 0) return null;
            const sub = sumAgeing(rows);
            return (
              <React.Fragment key={tt}>
                {rows.map((r) => (
                  <tr key={`${r.ticketType}-${r.assignmentGroupName}`} className="bg-white hover:bg-slate-50/80">
                    <td className={`${tdBase} whitespace-nowrap font-medium`}>{r.ticketType}</td>
                    <td className={`${tdBase} max-w-[14rem] truncate`} title={r.assignmentGroupName}>
                      {r.assignmentGroupName}
                    </td>
                    <td className={tdNum}>{r.days2to5}</td>
                    <td className={tdNum}>{r.days6to10}</td>
                    <td className={tdNum}>{r.days11to15}</td>
                    <td className={tdNum}>{r.days16to30}</td>
                    <td className={tdNum}>{r.daysOver30}</td>
                    <td className={tdNum}>{r.rowTotal}</td>
                  </tr>
                ))}
                <tr className={KPI_DASHBOARD_TABLE_SUBTOTAL_ROW}>
                  <td className={`${tdBase} whitespace-nowrap`} colSpan={2}>
                    {tt} TOTAL
                  </td>
                  <td className={tdNum}>{sub.days2to5}</td>
                  <td className={tdNum}>{sub.days6to10}</td>
                  <td className={tdNum}>{sub.days11to15}</td>
                  <td className={tdNum}>{sub.days16to30}</td>
                  <td className={tdNum}>{sub.daysOver30}</td>
                  <td className={tdNum}>{sub.rowTotal}</td>
                </tr>
              </React.Fragment>
            );
          })}
          <tr className={KPI_DASHBOARD_TABLE_GRAND_ROW}>
            <td className={`${tdBase} whitespace-nowrap`} colSpan={2}>
              GRAND TOTAL
            </td>
            <td className={tdNum}>{ageingGrand.days2to5}</td>
            <td className={tdNum}>{ageingGrand.days6to10}</td>
            <td className={tdNum}>{ageingGrand.days11to15}</td>
            <td className={tdNum}>{ageingGrand.days16to30}</td>
            <td className={tdNum}>{ageingGrand.daysOver30}</td>
            <td className={tdNum}>{ageingGrand.rowTotal}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export interface KpiOperationalLastUpdatedTableProps {
  lastUpdatedByGroup: KpiLastUpdatedRow[];
}

export const KpiOperationalLastUpdatedTable: React.FC<KpiOperationalLastUpdatedTableProps> = ({
  lastUpdatedByGroup,
}) => {
  const lastGrand = useMemo(() => sumLast(lastUpdatedByGroup), [lastUpdatedByGroup]);
  const lastByType = useMemo(() => {
    const map = new Map<string, KpiLastUpdatedRow[]>();
    for (const row of lastUpdatedByGroup) {
      const list = map.get(row.ticketType) ?? [];
      list.push(row);
      map.set(row.ticketType, list);
    }
    return map;
  }, [lastUpdatedByGroup]);

  if (lastUpdatedByGroup.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-3 py-4 text-xs text-slate-500">
        No open tickets for this summary with the current filters.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[520px] border-collapse text-xs sm:text-sm">
        <thead>
          <tr>
            <th className={KPI_DASHBOARD_TABLE_TH}>Type</th>
            <th className={KPI_DASHBOARD_TABLE_TH}>Assignment group</th>
            <th className={KPI_DASHBOARD_TABLE_TH_CENTER}>0–24 hours</th>
            <th className={KPI_DASHBOARD_TABLE_TH_CENTER}>&gt; 24 hours</th>
            <th className={KPI_DASHBOARD_TABLE_TH_CENTER}>Grand total</th>
          </tr>
        </thead>
        <tbody>
          {TYPE_ORDER.map((tt) => {
            const rows = lastByType.get(tt) ?? [];
            if (rows.length === 0) return null;
            const sub = sumLast(rows);
            return (
              <React.Fragment key={`last-${tt}`}>
                {rows.map((r) => (
                  <tr key={`${r.ticketType}-${r.assignmentGroupName}-lu`} className="bg-white hover:bg-slate-50/80">
                    <td className={`${tdBase} whitespace-nowrap font-medium`}>{r.ticketType}</td>
                    <td className={`${tdBase} max-w-[14rem] truncate`} title={r.assignmentGroupName}>
                      {r.assignmentGroupName}
                    </td>
                    <td className={tdNum}>{r.within24h}</td>
                    <td className={tdNum}>{r.over24h}</td>
                    <td className={tdNum}>{r.rowTotal}</td>
                  </tr>
                ))}
                <tr className={KPI_DASHBOARD_TABLE_SUBTOTAL_ROW}>
                  <td className={`${tdBase} whitespace-nowrap`} colSpan={2}>
                    {tt} TOTAL
                  </td>
                  <td className={tdNum}>{sub.within24h}</td>
                  <td className={tdNum}>{sub.over24h}</td>
                  <td className={tdNum}>{sub.rowTotal}</td>
                </tr>
              </React.Fragment>
            );
          })}
          <tr className={KPI_DASHBOARD_TABLE_GRAND_ROW}>
            <td className={`${tdBase} whitespace-nowrap`} colSpan={2}>
              GRAND TOTAL
            </td>
            <td className={tdNum}>{lastGrand.within24h}</td>
            <td className={tdNum}>{lastGrand.over24h}</td>
            <td className={tdNum}>{lastGrand.rowTotal}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

type SheetCell = string | number;

export type KpiExcelExport = { blob: Blob; fileName: string };

export function exportAgeingTableExcel(ageingByGroup: KpiOperationalAgeingRow[]): KpiExcelExport {
  const headers: SheetCell[] = [
    'Type',
    'Assignment group',
    '2-5 days',
    '6-10 days',
    '11-15 days',
    '16-30 days',
    '>30 days',
    'Grand total',
  ];
  const aoa: SheetCell[][] = [headers];

  const ageingByType = new Map<string, KpiOperationalAgeingRow[]>();
  for (const row of ageingByGroup) {
    const list = ageingByType.get(row.ticketType) ?? [];
    list.push(row);
    ageingByType.set(row.ticketType, list);
  }

  for (const tt of TYPE_ORDER) {
    const rows = ageingByType.get(tt) ?? [];
    if (rows.length === 0) continue;
    for (const r of rows) {
      aoa.push([
        r.ticketType,
        r.assignmentGroupName,
        r.days2to5,
        r.days6to10,
        r.days11to15,
        r.days16to30,
        r.daysOver30,
        r.rowTotal,
      ]);
    }
    const sub = sumAgeing(rows);
    aoa.push([
      `${tt} TOTAL`,
      '',
      sub.days2to5,
      sub.days6to10,
      sub.days11to15,
      sub.days16to30,
      sub.daysOver30,
      sub.rowTotal,
    ]);
  }
  const g = sumAgeing(ageingByGroup);
  aoa.push([
    'GRAND TOTAL',
    '',
    g.days2to5,
    g.days6to10,
    g.days11to15,
    g.days16to30,
    g.daysOver30,
    g.rowTotal,
  ]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [14, 38, 11, 11, 11, 11, 11, 13]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Summary');
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `kpi_open_backlog_ageing_summary_${stamp}.xlsx`;
  return { blob: workbookToXlsxBlob(wb), fileName };
}

export function exportLastUpdatedTableExcel(lastUpdatedByGroup: KpiLastUpdatedRow[]): KpiExcelExport {
  const headers: SheetCell[] = ['Type', 'Assignment group', '0-24 hours', '> 24 hours', 'Grand total'];
  const aoa: SheetCell[][] = [headers];

  const lastByType = new Map<string, KpiLastUpdatedRow[]>();
  for (const row of lastUpdatedByGroup) {
    const list = lastByType.get(row.ticketType) ?? [];
    list.push(row);
    lastByType.set(row.ticketType, list);
  }

  for (const tt of TYPE_ORDER) {
    const rows = lastByType.get(tt) ?? [];
    if (rows.length === 0) continue;
    for (const r of rows) {
      aoa.push([r.ticketType, r.assignmentGroupName, r.within24h, r.over24h, r.rowTotal]);
    }
    const sub = sumLast(rows);
    aoa.push([`${tt} TOTAL`, '', sub.within24h, sub.over24h, sub.rowTotal]);
  }
  const g = sumLast(lastUpdatedByGroup);
  aoa.push(['GRAND TOTAL', '', g.within24h, g.over24h, g.rowTotal]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [14, 38, 14, 14, 13]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Summary');
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `kpi_open_backlog_last_updated_summary_${stamp}.xlsx`;
  return { blob: workbookToXlsxBlob(wb), fileName };
}
